// AccountFAQ.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Collapse, Input, Typography } from 'antd';
import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { supportArticleIndex, supportArticlePath } from '../supportArticles';
import { parseSupportArticle, type SupportArticleSection } from '../utils/supportArticleParser';
import SupportArticleInline from '../components/SupportArticleInline';
import './AccountFAQ.css';

const { Panel } = Collapse;
const { Text, Title } = Typography;

type FaqCategoryFilter = 'all' | 'billing' | 'credits' | 'referrals';

type ParsedFaqArticle = {
  slug: string;
  title: string;
  summary: string;
  sections: SupportArticleSection[];
  order: number;
  searchText: string;
  category: Exclude<FaqCategoryFilter, 'all'>;
};

const ACCOUNT_FAQ_FILES = [
  'faq_can_i_upgrade_or_downgrade_anytime.md',
  'faq_do_subscription_credits_roll_over.md',
  'faq_when_does_my_subscription_renew.md',
  'faq_do_plan_changes_affect_credit_expiration.md',
  'faq_why_renew_now_is_disabled.md',
  'faq_can_referral_code_be_used_on_any_plan.md',
];

const ACCOUNT_FAQ_CATEGORY_BY_FILE: Record<string, ParsedFaqArticle['category']> = {
  'faq_can_i_upgrade_or_downgrade_anytime.md': 'billing',
  'faq_when_does_my_subscription_renew.md': 'billing',
  'faq_why_renew_now_is_disabled.md': 'billing',
  'faq_do_subscription_credits_roll_over.md': 'credits',
  'faq_do_plan_changes_affect_credit_expiration.md': 'credits',
  'faq_can_referral_code_be_used_on_any_plan.md': 'referrals',
};

const FAQ_CATEGORY_LABELS: Record<FaqCategoryFilter, string> = {
  all: 'All',
  billing: 'Billing',
  credits: 'Credits',
  referrals: 'Referrals',
};

const FAQ_FILTERS: FaqCategoryFilter[] = ['all', 'billing', 'credits', 'referrals'];

const FaqArticleHeader: React.FC<{ article: ParsedFaqArticle }> = ({ article }) => (
  <div className="account-faq-article-header">
    <span className="account-faq-article-title">{article.title}</span>
    {article.summary && <Text type="secondary">{article.summary}</Text>}
  </div>
);

const FaqStatePanel: React.FC<{
  tone?: 'default' | 'warning';
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ tone = 'default', title, description, action }) => (
  <div className={`account-faq-state account-faq-state--${tone}`}>
    <Text strong>{title}</Text>
    {description && <Text type="secondary">{description}</Text>}
    {action && <div className="account-faq-state-action">{action}</div>}
  </div>
);

export default function AccountFAQ(): JSX.Element {
  const [faqArticles, setFaqArticles] = useState<ParsedFaqArticle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [faqQuery, setFaqQuery] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<FaqCategoryFilter>('all');

  const loadFaqs = useCallback(async (isActive: () => boolean = () => true) => {
    setLoading(true);
    setError(null);
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
      if (isActive()) {
        setError('FAQs are unavailable in this environment.');
        setLoading(false);
      }
      return;
    }

    try {
      const entries = ACCOUNT_FAQ_FILES
        .map((file) => supportArticleIndex.find((entry) => entry.file === file))
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

      const results = await Promise.all(
        entries.map(async (entry) => {
          const response = await fetch(supportArticlePath(entry.file));
          if (!response.ok) {
            throw new Error(`Failed to load ${entry.file}`);
          }
          const markdown = await response.text();
          const parsed = parseSupportArticle(markdown, entry.slug);
          return {
            ...parsed,
            category: ACCOUNT_FAQ_CATEGORY_BY_FILE[entry.file] || 'billing',
            order: entry.order,
          };
        }),
      );

      if (isActive()) {
        setFaqArticles(results.sort((a, b) => a.order - b.order));
        setLoading(false);
      }
    } catch {
      if (isActive()) {
        setError('Unable to load account FAQs right now.');
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let isActive = true;
    void loadFaqs(() => isActive);
    return () => {
      isActive = false;
    };
  }, [loadFaqs]);

  const filteredFaqArticles = useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    return faqArticles.filter((article) => {
      if (activeCategory !== 'all' && article.category !== activeCategory) {
        return false;
      }
      if (!q) {
        return true;
      }
      return article.searchText.includes(q);
    });
  }, [activeCategory, faqArticles, faqQuery]);

  const resultLabel = useMemo(() => {
    if (filteredFaqArticles.length === 0) return 'No matches';
    return `${filteredFaqArticles.length} answer${filteredFaqArticles.length === 1 ? '' : 's'}`;
  }, [filteredFaqArticles.length]);

  const clearSearch = () => setFaqQuery('');

  const renderSection = (section: SupportArticleSection, index: number) => (
    <div key={`${section.heading}-${index}`} className="account-faq-answer-section">
      <h4>{section.heading}</h4>
      {section.paragraphs.map((paragraph, paragraphIndex) => (
        <p key={`${section.heading}-p-${paragraphIndex}`}>
          <SupportArticleInline text={paragraph} />
        </p>
      ))}
      {section.listItems.length > 0 && section.listType === 'ordered' && (
        <ol>
          {section.listItems.map((item, itemIndex) => (
            <li key={`${section.heading}-item-${itemIndex}`}>
              <SupportArticleInline text={item} />
            </li>
          ))}
        </ol>
      )}
      {section.listItems.length > 0 && section.listType !== 'ordered' && (
        <ul>
          {section.listItems.map((item, itemIndex) => (
            <li key={`${section.heading}-item-${itemIndex}`}>
              <SupportArticleInline text={item} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="account-faq-shell">
      <Card
        className="account-faq-card"
        title={
          <div className="account-faq-card-title">
            <span className="account-faq-card-icon"><QuestionCircleOutlined /></span>
            <div className="account-faq-card-copy">
              <span className="account-faq-kicker">Help</span>
              <Title level={2}>Account FAQ</Title>
              <Text type="secondary">Find answers about billing, credits, referrals, gifts, and account settings.</Text>
            </div>
          </div>
        }
        extra={
          <Link to="/support" className="account-faq-support-link">
            Support center
          </Link>
        }
      >
        {!loading && !error && (
          <div className="account-faq-controls">
            <Input.Search
              allowClear
              placeholder="Search account questions"
              onChange={(e) => setFaqQuery(e.target.value)}
              value={faqQuery}
              className="account-faq-search"
            />
            <div className="account-faq-control-row">
              <div className="account-faq-filter-pills" role="group" aria-label="Account FAQ category filter">
                {FAQ_FILTERS.map((filter) => (
                  <Button
                    key={filter}
                    size="small"
                    type={activeCategory === filter ? 'primary' : 'default'}
                    onClick={() => setActiveCategory(filter)}
                  >
                    {FAQ_CATEGORY_LABELS[filter]}
                  </Button>
                ))}
              </div>
              <span className="account-faq-result-pill">{resultLabel}</span>
            </div>
          </div>
        )}

        {loading && <FaqStatePanel title="Loading account FAQs..." />}
        {error && (
          <FaqStatePanel
            tone="warning"
            title={error}
            action={
              <Button icon={<ReloadOutlined />} onClick={() => loadFaqs()} className="account-faq-inline-action">
                Retry
              </Button>
            }
          />
        )}
        {!loading && !error && (
          <>
            {filteredFaqArticles.length > 0 ? (
              <Collapse bordered={false} accordion={false} expandIconPosition="end" className="account-faq-collapse">
                {filteredFaqArticles.map((article) => (
                  <Panel
                    header={<FaqArticleHeader article={article} />}
                    key={article.slug}
                    className="account-faq-panel"
                  >
                    {article.sections.map(renderSection)}
                  </Panel>
                ))}
              </Collapse>
            ) : (
              <FaqStatePanel
                title="No account FAQs matched your search."
                description="Try a shorter keyword or switch categories."
                action={
                  faqQuery ? (
                    <Button onClick={clearSearch} className="account-faq-inline-action">
                      Clear search
                    </Button>
                  ) : null
                }
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
